import React from 'react';
import type { CalendarFilterChip } from '../../calendar/filters';
import { Button } from '../Button/Button';
import { Icon } from '../Icon/Icon';

export interface CalendarFilterBarProps {
  chips: readonly CalendarFilterChip[];
  onRemove: (id: string) => void;
  onClearAll: () => void;
  /** Appointments that pass the filters / all appointments in the view. Omit for views without a count. */
  matched?: number;
  total?: number;
}

/** Active-filter chips + result count, shown above the grid only while a filter is on. */
export function CalendarFilterBar({
  chips,
  onRemove,
  onClearAll,
  matched,
  total,
}: CalendarFilterBarProps) {
  if (chips.length === 0) return null;

  const hasCount = matched !== undefined && total !== undefined;
  const nothingMatches = hasCount && total > 0 && matched === 0;

  return (
    <div className="flex shrink-0 flex-wrap items-center gap-2 px-[30px] pb-3">
      {hasCount ? (
        <span
          role="status"
          className="mr-1 text-[12.5px] font-semibold text-[var(--ink-1)]"
        >
          {nothingMatches
            ? 'No appointments match these filters'
            : `Showing ${String(matched)} of ${String(total)}`}
        </span>
      ) : null}
      {chips.map((chip) => (
        <span
          key={chip.id}
          className="inline-flex items-center gap-1 rounded-[var(--r-pill)] border border-[var(--ai-3)] bg-[var(--ai-tint)] py-1 pl-3 pr-1.5 text-xs font-semibold text-[var(--ai-3)]"
        >
          {chip.label}
          <button
            type="button"
            aria-label={`Remove filter: ${chip.label}`}
            onClick={() => {
              onRemove(chip.id);
            }}
            className="inline-flex h-4 w-4 cursor-pointer items-center justify-center rounded-full text-[var(--ai-3)] hover:bg-[var(--cream-2)]"
          >
            <Icon kind="x" size={10} stroke={2.6} />
          </button>
        </span>
      ))}
      <Button variant="ghost" size="sm" onClick={onClearAll}>
        {nothingMatches ? 'Clear filters' : 'Clear all'}
      </Button>
    </div>
  );
}
